import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import {
    ApplicableMetricLevel,
    downloadReviewEntityMetricsTemplate,
    getReviewEntityMetricLevelConfig,
    MetricLevelConfig,
    MetricLevelInstance
} from '../../services/reviewEntityMetricLevelService';
import { getAllMetricLevels, MetricLevel } from '../../services/metricLevelService';
import { getApiErrorMessage } from '../../services/api';
import { PROJECT_METRIC_LEVEL } from '../../utils/metricLevelUtils';

const PROJECT_LEVEL_OPTION: ApplicableMetricLevel = {
    id: 0,
    name: PROJECT_METRIC_LEVEL,
    display_label: 'Entire Project (all leaf instances)',
    is_period_based: false,
    level_number: 0
};

export interface MetricsTemplateEntityOption {
    id: number;
    name: string;
    org_entity_code?: string | null;
}

export interface MetricsTemplateLevelOption {
    id: number;
    name: string;
    display_label: string;
    is_period_based?: boolean;
    level_number: number;
}

interface MetricsTemplateDownloadDialogProps {
    open: boolean;
    onClose: () => void;
    /** When set, entity is fixed (e.g. from entity detail page). */
    entityId?: number;
    entityName?: string;
    /** Used when entityId is not fixed — user picks from this list. */
    entities?: MetricsTemplateEntityOption[];
    /**
     * Entity-type (or other) level catalog used when the config API returns
     * applicable_metric_level_ids but empty applicable_metric_levels enrichment.
     */
    fallbackLevels?: MetricsTemplateLevelOption[];
}

/** Build a usable level list even when API enrichment is sparse. */
export function resolveTemplateLevels(
    config: MetricLevelConfig,
    fallbackLevels: MetricsTemplateLevelOption[] = []
): ApplicableMetricLevel[] {
    if ((config.applicable_metric_levels || []).length > 0) {
        return config.applicable_metric_levels;
    }

    const fallbackById = new Map(
        fallbackLevels.map(level => [Number(level.id), level])
    );
    const ids = (config.applicable_metric_level_ids || []).map(Number).filter(n => !Number.isNaN(n));

    if (ids.length > 0) {
        const fromIds: ApplicableMetricLevel[] = [];
        ids.forEach((id, index) => {
            const fb = fallbackById.get(id);
            const fromInst = (config.instances || []).find(
                inst => Number(inst.metric_level_id) === id
            );
            const name = fb?.name || fromInst?.metric_level_name;
            if (!name) {
                return;
            }
            fromIds.push({
                id,
                name,
                display_label:
                    fb?.display_label ||
                    fromInst?.metric_level_display_label ||
                    name,
                is_period_based: Boolean(
                    fb?.is_period_based ?? fromInst?.is_period_based
                ),
                level_number: fb?.level_number ?? index + 1
            });
        });
        if (fromIds.length > 0) {
            return fromIds;
        }
    }

    // Last resort: unique levels referenced by instances
    const byId = new Map<number, ApplicableMetricLevel>();
    (config.instances || []).forEach(inst => {
        if (inst.metric_level_id == null) return;
        const id = Number(inst.metric_level_id);
        if (Number.isNaN(id) || byId.has(id)) return;
        const fb = fallbackById.get(id);
        byId.set(id, {
            id,
            name: fb?.name || inst.metric_level_name || `LEVEL_${id}`,
            display_label:
                fb?.display_label ||
                inst.metric_level_display_label ||
                inst.metric_level_name ||
                fb?.name ||
                `Level ${id}`,
            is_period_based: Boolean(fb?.is_period_based ?? inst.is_period_based),
            level_number: fb?.level_number ?? byId.size + 1
        });
    });
    return Array.from(byId.values());
}

const MetricsTemplateDownloadDialog: React.FC<MetricsTemplateDownloadDialogProps> = ({
    open,
    onClose,
    entityId: fixedEntityId,
    entityName: fixedEntityName,
    entities = [],
    fallbackLevels = []
}) => {
    const [selectedEntityId, setSelectedEntityId] = useState<number | null>(fixedEntityId ?? null);
    const [levels, setLevels] = useState<ApplicableMetricLevel[]>([]);
    const [instances, setInstances] = useState<MetricLevelInstance[]>([]);
    const [metricLevelName, setMetricLevelName] = useState(PROJECT_METRIC_LEVEL);
    const [selectedInstances, setSelectedInstances] = useState<MetricLevelInstance[]>([]);
    const [mode, setMode] = useState<'blank' | 'sample'>('blank');
    const [loadingLevels, setLoadingLevels] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const entityId = fixedEntityId ?? selectedEntityId;

    useEffect(() => {
        if (!open) return;
        setError(null);
        setMetricLevelName(PROJECT_METRIC_LEVEL);
        setSelectedInstances([]);
        setMode('blank');
        if (fixedEntityId != null) {
            setSelectedEntityId(fixedEntityId);
        }
    }, [open, fixedEntityId]);

    const fallbackKey = useMemo(
        () => fallbackLevels.map(l => `${l.id}:${l.name}`).join('|'),
        [fallbackLevels]
    );

    useEffect(() => {
        if (!open || entityId == null) {
            setLevels([]);
            setInstances([]);
            return;
        }
        let cancelled = false;
        const load = async () => {
            setLoadingLevels(true);
            setError(null);
            try {
                const [config, catalog] = await Promise.all([
                    getReviewEntityMetricLevelConfig(entityId),
                    getAllMetricLevels().catch(() => [] as MetricLevel[])
                ]);
                if (cancelled) return;
                const catalogAsFallback: MetricsTemplateLevelOption[] = (catalog || []).map(
                    (level, index) => ({
                        id: level.id,
                        name: level.name,
                        display_label: level.display_label,
                        is_period_based: level.is_period_based,
                        level_number: index + 1
                    })
                );
                const mergedFallback = [...fallbackLevels];
                const seen = new Set(mergedFallback.map(l => Number(l.id)));
                catalogAsFallback.forEach(level => {
                    if (!seen.has(Number(level.id))) {
                        mergedFallback.push(level);
                        seen.add(Number(level.id));
                    }
                });
                const applicable = resolveTemplateLevels(config, mergedFallback);
                setLevels(applicable);
                setInstances(config.instances || []);
                setMetricLevelName(PROJECT_METRIC_LEVEL);
                setSelectedInstances([]);
            } catch (err: unknown) {
                if (!cancelled) {
                    setLevels([]);
                    setInstances([]);
                    setError(getApiErrorMessage(err, 'Failed to load metric levels for this entity'));
                }
            } finally {
                if (!cancelled) setLoadingLevels(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, entityId, fallbackKey]);

    const levelOptions = useMemo(
        () => [PROJECT_LEVEL_OPTION, ...levels],
        [levels]
    );

    const selectedLevel = useMemo(
        () => levelOptions.find(l => l.name === metricLevelName) || null,
        [levelOptions, metricLevelName]
    );

    const isProjectScope = metricLevelName === PROJECT_METRIC_LEVEL;

    const instancesForLevel = useMemo(() => {
        if (!selectedLevel || isProjectScope) return [];
        return instances.filter(
            inst =>
                Number(inst.metric_level_id) === Number(selectedLevel.id) ||
                (inst.metric_level_name || '').toUpperCase() === selectedLevel.name.toUpperCase()
        );
    }, [instances, selectedLevel, isProjectScope]);

    const requiresInstance = Boolean(selectedLevel) && !isProjectScope;

    useEffect(() => {
        if (!requiresInstance) {
            setSelectedInstances([]);
            return;
        }
        if (instancesForLevel.length === 1) {
            setSelectedInstances(instancesForLevel);
            return;
        }
        setSelectedInstances(prev =>
            prev.filter(sel => instancesForLevel.some(i => i.id === sel.id))
        );
    }, [requiresInstance, instancesForLevel, metricLevelName]);

    const selectedEntityLabel = useMemo(() => {
        if (fixedEntityName) return fixedEntityName;
        const match = entities.find(e => e.id === selectedEntityId);
        if (!match) return '';
        return match.org_entity_code
            ? `${match.name} (${match.org_entity_code})`
            : match.name;
    }, [fixedEntityName, entities, selectedEntityId]);

    const selectedEntityNameForFile = useMemo(() => {
        if (fixedEntityName) return fixedEntityName;
        return entities.find(e => e.id === selectedEntityId)?.name || '';
    }, [fixedEntityName, entities, selectedEntityId]);

    const handleDownload = async () => {
        if (entityId == null || !metricLevelName) {
            setError('Select a review entity and metric level');
            return;
        }
        if (requiresInstance && selectedInstances.length === 0) {
            setError('Select at least one level instance (or choose Entire Project)');
            return;
        }
        setDownloading(true);
        setError(null);
        try {
            await downloadReviewEntityMetricsTemplate(
                entityId,
                metricLevelName,
                mode === 'sample',
                selectedEntityNameForFile || undefined,
                requiresInstance ? selectedInstances.map(i => i.id) : undefined,
                requiresInstance ? selectedInstances.map(i => i.name) : undefined
            );
            onClose();
        } catch (err: unknown) {
            setError(getApiErrorMessage(err, 'Failed to download metrics template'));
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Generate Metrics Data Template</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    {error && (
                        <Alert severity="error" onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}
                    {fixedEntityId != null ? (
                        <TextField
                            label="Review Entity"
                            value={selectedEntityLabel}
                            fullWidth
                            InputProps={{ readOnly: true }}
                        />
                    ) : (
                        <Autocomplete
                            options={entities}
                            getOptionLabel={(option) =>
                                option.org_entity_code
                                    ? `${option.name} (${option.org_entity_code})`
                                    : option.name
                            }
                            value={entities.find(e => e.id === selectedEntityId) || null}
                            onChange={(_, value) => setSelectedEntityId(value?.id ?? null)}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            renderInput={(params) => (
                                <TextField {...params} label="Review Entity" required />
                            )}
                        />
                    )}

                    <FormControl fullWidth required disabled={loadingLevels || entityId == null}>
                        <InputLabel id="metric-level-template-label">Metric Level</InputLabel>
                        <Select
                            labelId="metric-level-template-label"
                            label="Metric Level"
                            value={metricLevelName}
                            onChange={(e) => {
                                setMetricLevelName(e.target.value as string);
                                setSelectedInstances([]);
                            }}
                        >
                            {levelOptions.map(level => (
                                <MenuItem key={`${level.id}-${level.name}`} value={level.name}>
                                    {level.name === PROJECT_METRIC_LEVEL
                                        ? level.display_label
                                        : `L${level.level_number}: ${level.display_label} (${level.name})${
                                              level.is_period_based ? ' — periodic' : ''
                                          }`}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {requiresInstance && (
                        <Autocomplete
                            multiple
                            options={instancesForLevel}
                            value={selectedInstances}
                            onChange={(_, value) => setSelectedInstances(value)}
                            getOptionLabel={(option) =>
                                option.start_date && option.end_date
                                    ? `${option.name} (${option.start_date} → ${option.end_date})`
                                    : option.name
                            }
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            disabled={loadingLevels || !metricLevelName || instancesForLevel.length === 0}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Level Instance(s)"
                                    required
                                    helperText="Multi-select. Coarser levels expand to all descendant leaf rows."
                                />
                            )}
                        />
                    )}

                    <Alert severity="info">
                        {isProjectScope
                            ? 'Entire Project creates one row per finest-level instance (e.g. every Sprint), with parent hierarchy columns filled.'
                            : 'Selecting a coarser instance (e.g. Milestone) creates one row per descendant leaf under that selection. Fill Metric Period Start/End for the measurement window; Metric Capture Date is when data was entered.'}
                    </Alert>

                    {requiresInstance && metricLevelName && instancesForLevel.length === 0 && (
                        <Alert severity="warning">
                            No instances configured for this level. Add instances on the entity
                            Metric Levels panel first.
                        </Alert>
                    )}

                    {loadingLevels && (
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <CircularProgress size={24} />
                        </Box>
                    )}

                    {!loadingLevels && entityId != null && levels.length === 0 && (
                        <Alert severity="info">
                            This entity has no applicable metric levels. Configure them on the entity
                            Metric Levels panel first.
                        </Alert>
                    )}

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            Template contents
                        </Typography>
                        <RadioGroup
                            value={mode}
                            onChange={(e) => setMode(e.target.value as 'blank' | 'sample')}
                        >
                            <FormControlLabel
                                value="blank"
                                control={<Radio />}
                                label={
                                    isProjectScope
                                        ? 'Blank — one row per finest leaf under the project'
                                        : 'Blank — one row per descendant leaf under the selection'
                                }
                            />
                            <FormControlLabel
                                value="sample"
                                control={<Radio />}
                                label={
                                    isProjectScope
                                        ? 'With sample values — one row per finest leaf under the project'
                                        : 'With sample values — one row per descendant leaf under the selection'
                                }
                            />
                        </RadioGroup>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={downloading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                    onClick={handleDownload}
                    disabled={
                        downloading ||
                        entityId == null ||
                        !metricLevelName ||
                        (levels.length === 0 && !isProjectScope) ||
                        (requiresInstance && selectedInstances.length === 0)
                    }
                >
                    {downloading ? 'Generating…' : 'Download'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MetricsTemplateDownloadDialog;
